import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Wrench,
  ShoppingBag,
  BookOpen,
  Wallet,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { ActivityTimeline } from './ActivityTimeline';
import { apiGet } from '../../lib/api-client';
import { repairService } from '../../services/repair.service';

interface DashboardStats {
  totalRepairs: number;
  activeRepairs: number;
  completedRepairs: number;
  totalOrders: number;
  pendingOrders: number;
  deliveredOrders: number;
  enrolledCourses: number;
  coursesInProgress: number;
  certificatesEarned: number;
  walletBalance: number;
}

// Placeholder for recent repairs data
interface RecentRepair {
  id: string;
  deviceName: string;
  status: 'IN_PROGRESS' | 'WAITING_PARTS' | 'COMPLETED' | 'SUBMITTED';
  date: string;
}

// Placeholder for recent orders
interface RecentOrder {
  id: string;
  productName: string;
  status: 'PROCESSING' | 'SHIPPED' | 'DELIVERED';
  amount: number;
  date: string;
}

// Placeholder for course progress
interface CourseProgress {
  id: string;
  name: string;
  progress: number;
}

const fetchDashboardStats = async (): Promise<DashboardStats> => {
  return apiGet<DashboardStats>('/dashboard/stats');
};

// Real recent repairs fetched from the API
const fetchRecentRepairs = async (): Promise<RecentRepair[]> => {
  const repairs = await repairService.getMyRepairs();
  return repairs.slice(0, 5).map((repair) => ({
    id: String(repair.id),
    deviceName: repair.repairNumber,
    status: repair.status as RecentRepair['status'],
    date: repair.createdAt,
  }));
};

// Orders are not modeled in the API yet — return an empty (real) state.
const fetchRecentOrders = async (): Promise<RecentOrder[]> => {
  return [];
};

// Enrollments are not modeled in the API yet — return an empty (real) state.
const fetchCourseProgress = async (): Promise<CourseProgress[]> => {
  return [];
};

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'orange' | 'purple';
  link?: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, link, isLoading: statLoading }) => {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-500',
    green: 'bg-green-500/10 text-green-500',
    orange: 'bg-orange-500/10 text-orange-500',
    purple: 'bg-purple-500/10 text-purple-500',
  };

  const content = (
    <Card className="transition-all hover:shadow-md hover:-translate-y-0.5">
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-sm text-[rgb(var(--light-secondary))]">{title}</p>
          <p className="text-2xl font-bold text-[rgb(var(--foreground))]">
            {statLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-[rgb(var(--light-secondary))]" />
            ) : (
              value
            )}
          </p>
        </div>
        <div className={`rounded-full p-3 ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
};

export const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: fetchDashboardStats,
  });

  const { data: recentRepairs, isLoading: repairsLoading } = useQuery({
    queryKey: ['recent-repairs'],
    queryFn: fetchRecentRepairs,
  });

  const { data: recentOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ['recent-orders'],
    queryFn: fetchRecentOrders,
  });

  const { data: courses, isLoading: coursesLoading } = useQuery({
    queryKey: ['course-progress'],
    queryFn: fetchCourseProgress,
  });

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const greeting = (() => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const quickActions = [
    { label: 'Book a Repair', path: '/dashboard/repairs/new', icon: Wrench },
    { label: 'Shop Devices', path: '/shop', icon: ShoppingBag },
    { label: 'Continue Learning', path: '/dashboard/courses', icon: BookOpen },
  ];

  const statusMap = {
    IN_PROGRESS: { label: 'In Progress', color: 'bg-orange-500/10 text-orange-500' },
    WAITING_PARTS: { label: 'Waiting', color: 'bg-yellow-500/10 text-yellow-500' },
    COMPLETED: { label: 'Completed', color: 'bg-green-500/10 text-green-500' },
    SUBMITTED: { label: 'Submitted', color: 'bg-blue-500/10 text-blue-500' },
  };

  const orderStatusMap = {
    PROCESSING: { label: 'Processing', color: 'bg-yellow-500/10 text-yellow-500' },
    SHIPPED: { label: 'Shipped', color: 'bg-blue-500/10 text-blue-500' },
    DELIVERED: { label: 'Delivered', color: 'bg-green-500/10 text-green-500' },
  };

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 dark:from-primary/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[rgb(var(--foreground))]">
              {greeting}, {user?.name}!
            </h1>
            <p className="text-[rgb(var(--light-secondary))]">
              Welcome back to your EdyTech dashboard
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link to="/dashboard/repairs/new">
              <Button variant="primary" size="md">
                <Wrench className="mr-2 h-4 w-4" />
                Book a Repair
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Link key={action.path} to={action.path}>
              <Card className="flex items-center justify-center space-x-2 p-4 transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer">
                <Icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{action.label}</span>
                <ArrowRight className="h-4 w-4 text-[rgb(var(--light-secondary))]" />
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Repairs"
          value={stats?.activeRepairs || 0}
          icon={Wrench}
          color="orange"
          link="/dashboard/repairs"
          isLoading={statsLoading}
        />
        <StatCard
          title="Pending Orders"
          value={stats?.pendingOrders || 0}
          icon={ShoppingBag}
          color="blue"
          link="/orders"
          isLoading={statsLoading}
        />
        <StatCard
          title="Courses in Progress"
          value={stats?.coursesInProgress || 0}
          icon={BookOpen}
          color="purple"
          link="/dashboard/courses"
          isLoading={statsLoading}
        />
        <StatCard
          title="Wallet Balance"
          value={`GHS ${stats?.walletBalance.toFixed(2) || '0.00'}`}
          icon={Wallet}
          color="green"
          isLoading={statsLoading}
        />
      </div>

      {/* Course Progress */}
      <Card>
        <CardTitle className="flex items-center justify-between">
          <span>Course Progress</span>
          <Link to="/dashboard/courses" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </CardTitle>
        <CardContent className="space-y-4">
          {coursesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : courses && courses.length > 0 ? (
            courses.map((course) => (
              <div key={course.id}>
                <div className="flex items-center justify-between text-sm">
                  <span>{course.name}</span>
                  <span className="text-[rgb(var(--light-secondary))]">{course.progress}%</span>
                </div>
                <div className="mt-1 h-2 w-full rounded-full bg-[rgb(var(--border))]">
                  <div
                    className="h-2 rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${course.progress}%` }}
                  />
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-sm text-[rgb(var(--light-secondary))]">
              No courses enrolled yet. Browse our courses to start learning!
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Repairs */}
        <Card className="lg:col-span-1">
          <CardTitle className="flex items-center justify-between">
            <span>Recent Repairs</span>
            <Link to="/dashboard/repairs" className="text-sm text-primary hover:underline">
              View all
            </Link>
          </CardTitle>
          <CardContent className="space-y-3">
            {repairsLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : recentRepairs && recentRepairs.length > 0 ? (
              recentRepairs.map((repair) => {
                const status = statusMap[repair.status] || { label: repair.status, color: 'bg-gray-500/10 text-gray-500' };
                return (
                  <div key={repair.id} className="flex items-center justify-between rounded-lg border border-[rgb(var(--border))] p-3">
                    <div>
                      <p className="text-sm font-medium">{repair.deviceName}</p>
                      <p className="text-xs text-[rgb(var(--light-secondary))]">
                        {new Date(repair.date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-sm text-[rgb(var(--light-secondary))]">
                No repairs yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="lg:col-span-1">
          <CardTitle className="flex items-center justify-between">
            <span>Recent Orders</span>
            <Link to="/orders" className="text-sm text-primary hover:underline">
              View all
            </Link>
          </CardTitle>
          <CardContent className="space-y-3">
            {ordersLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : recentOrders && recentOrders.length > 0 ? (
              recentOrders.map((order) => {
                const status = orderStatusMap[order.status] || { label: order.status, color: 'bg-gray-500/10 text-gray-500' };
                return (
                  <div key={order.id} className="flex items-center justify-between rounded-lg border border-[rgb(var(--border))] p-3">
                    <div>
                      <p className="text-sm font-medium">{order.productName}</p>
                      <p className="text-xs text-[rgb(var(--light-secondary))]">
                        Order #{order.id}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-primary">${order.amount}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-sm text-[rgb(var(--light-secondary))]">
                No orders yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Activity Timeline */}
        <div className="lg:col-span-1">
          <ActivityTimeline />
        </div>
      </div>
    </div>
  );
};